/**
 * useMediaQuery Hook
 *
 * Custom hook to detect media query matches for responsive behaviour.
 * Returns a boolean indicating whether the media query matches.
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)')
 * const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
 */

import { useState, useEffect } from 'react'

/**
 * Hook to detect media query matches
 *
 * @param {string} query - CSS media query string
 * @returns {boolean} Whether the media query currently matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    // Initial check on mount (SSR-safe fallback to false)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia(query)

    // Update state if it doesn't match current value
    if (mediaQuery.matches !== matches) {
      setMatches(mediaQuery.matches)
    }

    // Handler for media query changes
    const handleChange = (event) => {
      setMatches(event.matches)
    }

    // Add listener for changes
    mediaQuery.addEventListener('change', handleChange)

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [query, matches])

  return matches
}

export default useMediaQuery
